import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import DatePicker from "react-multi-date-picker";
import PropTypes from "prop-types";

const TimePeriods = ({ handleApplyButton }) => {
  const datePickerRef = useRef(null);
  const [selectedType, setSelectedType] = useState("All");
  const [selectedDates, setSelectedDates] = useState([]);
  const [activeButton, setActiveButton] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const loadSavedDate = async () => {
      const savedDateType = localStorage.getItem("selectedDateType") || "All";
      const customDates = JSON.parse(localStorage.getItem("Custom")) || [];
      let generatedDates = [];
      if (savedDateType === "Custom" && customDates.length === 2) {
        // Use saved custom dates if they exist
        generatedDates = customDates;
      } else if (savedDateType) {
        // Generate dates for other types
        generatedDates = generateDateArray(savedDateType);
      }

      setSelectedType(savedDateType);
      localStorage.setItem("selectedDateType", "All");
      setSelectedDates(generatedDates.map((date) => new Date(date)));

      // Fetch enquiries based on the saved type and dates
      await handleApplyButton(generatedDates, savedDateType, 2);

      switch (savedDateType) {
        case "Today":
          setActiveButton(1);
          break;
        case "This Week":
          setActiveButton(2);
          break;
        case "This Month":
          setActiveButton(3);
          break;
        case "Yesterday":
          setActiveButton(4);
          break;
        case "Last 7 days":
          setActiveButton(5);
          break;
        case "Last Month":
          setActiveButton(6);
          break;
        case "Last Week":
          setActiveButton(7);
          break;
        case "Custom":
          setActiveButton(8);
          break;
        default:
          setActiveButton(0); // Default to "All"
          break;
      }
    };

    loadSavedDate();
  }, []);
  useEffect(() => {
    if (showModal && datePickerRef.current) {
      datePickerRef.current.openCalendar();
    }
  }, [showModal, datePickerRef]);

  const generateDateArray = (selectedType) => {
    const today = new Date();
    const result = [];

    switch (selectedType) {
      case "All":
        result.push(today.toISOString().split("T")[0]);
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(0);
        localStorage.setItem("selectedDateType", "All");
        localStorage.removeItem("Custom");
        break;

      case "Today":
        result.push(today.toISOString().split("T")[0]);
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(1);
        localStorage.setItem("selectedDateType", "Today");
        localStorage.removeItem("Custom");
        break;

      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        result.push(yesterday.toISOString().split("T")[0]);
        setActiveButton(4);
        setSelectedType(selectedType);
        setSelectedDates(result);
        localStorage.setItem("selectedDateType", "Yesterday");
        localStorage.removeItem("Custom");
        break;

      case "This Week":
        const startOfWeek = new Date(today);
        const endOfWeek = new Date(today);

        startOfWeek.setDate(today.getDate() - today.getDay()); // Start of the week (Sunday)
        endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // End of the week (Saturday)

        // Loop through the week to get all dates
        for (
          let date = new Date(startOfWeek);
          date <= endOfWeek;
          date.setDate(date.getDate() + 1)
        ) {
          result.push(new Date(date).toISOString().split("T")[0]); // Add each date to result
        }
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(2);
        localStorage.setItem("selectedDateType", "This Week");
        localStorage.removeItem("Custom"); // Clear custom dates from storage
        break;

      case "Last Week":
        const startOfLastWeek = new Date(today);
        const endOfLastWeek = new Date(today);

        startOfLastWeek.setDate(today.getDate() - today.getDay() - 7); // Start of last week (Sunday)
        endOfLastWeek.setDate(today.getDate() - today.getDay() - 1); // End of last week (Saturday)

        // Loop through the last week to get all dates
        for (
          let date = new Date(startOfLastWeek);
          date <= endOfLastWeek;
          date.setDate(date.getDate() + 1)
        ) {
          result.push(new Date(date).toISOString().split("T")[0]); // Add each date to result
        }
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(7);
        localStorage.setItem("selectedDateType", "Last Week");
        localStorage.removeItem("Custom"); // Clear custom dates from storage
        break;

      case "This Month":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 2); // First day of the current month
        const endOfMonth = today; // End date is today's date

        // Loop through the dates from start of the month to today
        for (
          let date = new Date(startOfMonth);
          date <= endOfMonth;
          date.setDate(date.getDate() + 1)
        ) {
          result.push(new Date(date).toISOString().split("T")[0]); // Add each date to the result array
        }
        result.push(today.toISOString().split("T")[0]);
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(3);
        localStorage.setItem("selectedDateType", "This Month");
        localStorage.removeItem("Custom"); // Clear custom dates
        break;

      case "Last Month":
        const startOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          2
        ); // First day of last month
        const endOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        ); // Last day of last month

        // Loop through the dates from start of last month to end of last month
        for (
          let date = new Date(startOfLastMonth);
          date <= endOfLastMonth;
          date.setDate(date.getDate() + 1)
        ) {
          result.push(new Date(date).toISOString().split("T")[0]); // Add each date to the result array
        }
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(6);
        localStorage.setItem("selectedDateType", "Last Month");
        localStorage.removeItem("Custom"); // Clear custom dates
        break;

      case "Last 7 days":
        for (let i = 6; i >= 0; i--) {
          const day = new Date(today);
          day.setDate(today.getDate() - i);
          result.push(day.toISOString().split("T")[0]);
          setSelectedType(selectedType);
          setSelectedDates(result);
          setActiveButton(5);
          localStorage.setItem("selectedDateType", "Last 7 days");
          localStorage.removeItem("Custom");
        }
        break;

      case "Custom":
        // Assuming custom dates are already selected or provided externally
        result.push(selectedDates);
        setSelectedType(selectedType);
        setSelectedDates(result);
        setActiveButton(8);
        break;

      default:
        break;
    }

    return result;
  };

  const handleFilterButtonClick = (type) => {
    console.log(type, "------->");
    const generatedDates = generateDateArray(type);
    if (datePickerRef.current && generatedDates.length > 0) {
      datePickerRef.current.startDate = new Date(generatedDates[0]);
      datePickerRef.current.endDate = new Date(
        generatedDates[generatedDates.length - 1]
      );
      datePickerRef.current.openCalendar();
    }
  };

  const handleApply = async (type) => {
    const dates = type === "Custom" ? selectedDates : generateDateArray(type);
    if (type === "Custom") {
      localStorage.setItem("Custom", JSON.stringify(selectedDates));
    }
    setShowModal(false);
    localStorage.setItem("selectedDateType", type);
    setSelectedType(type);
    setSelectedDates(dates.map((date) => new Date(date)));
    await handleApplyButton(dates, type);
  };
  return (
    <div>
      {/* Trigger Button */}
      <button
        className="btn btn-outline-secondary rounded"
        onClick={() => setShowModal(true)}
        style={{
          fontSize: "1rem",
          // fontWeight: "bold",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "200px",
          textAlign: "center",
        }}
      >
        {selectedType}
      </button>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Body>
          <div className="row">
            <div className="col-6">
              {/* Date Picker */}
              <div
                className="mb-4 w-75 d-flex justify-content-center"
                style={{
                  width: "500px",
                  height: "300px",
                }}
              >
                <DatePicker
                  range
                  dateSeparator=" to "
                  value={selectedDates}
                  onChange={setSelectedDates}
                  ref={datePickerRef}
                  className="date-picker p-3"
                  placeholder="Select Date"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #ced4da",
                    fontSize: "1rem",
                  }}
                />
              </div>
            </div>

            <div className="col-6 d-flex justify-content-center align-items-center">
              <div className="row w-100">
                <div
                  className="alert text-center p-1"
                  style={{
                    background: "#0dcaf0",
                    color: "white",
                    fontSize: "20px",
                  }}
                >
                  Select Time Period
                </div>
                {/* Column 1 */}
                <div className="col-6 d-flex flex-column">
                  {[
                    { label: "All", value: 0 },
                    { label: "Today", value: 1 },
                    { label: "Yesterday", value: 4 },
                    { label: "This Week", value: 2 },
                  ].map((button) => (
                    <button
                      key={button.value}
                      className={`btn ${
                        activeButton === button.value
                          ? "btn-info text-white"
                          : "btn-outline-secondary"
                      } rounded-pill px-4 py-2 w-100 mb-3`}
                      onClick={() => handleFilterButtonClick(button.label)}
                      style={{
                        fontSize: "0.9rem",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>

                {/* Column 2 */}
                <div className="col-6 d-flex flex-column">
                  {[
                    { label: "Last Week", value: 7 },
                    { label: "This Month", value: 3 },
                    { label: "Last Month", value: 6 },
                    { label: "Custom", value: 8 },
                  ].map((button) => (
                    <button
                      key={button.value}
                      className={`btn ${
                        activeButton === button.value
                          ? "btn-info text-white"
                          : "btn-outline-secondary"
                      } rounded-pill px-4 py-2 w-100 mb-3`}
                      onClick={() => handleFilterButtonClick(button.label)}
                      style={{
                        fontSize: "0.9rem",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            {/* Action Buttons */}
            <button
              className="btn btn-outline-secondary rounded-pill px-4 py-2 me-3"
              onClick={() => setShowModal(false)}
              style={{ fontSize: "0.9rem" }}
            >
              Cancel
            </button>
            <button
              className="btn btn-info text-white rounded-pill px-4 py-2"
              onClick={() => handleApply(selectedType)}
              style={{ fontSize: "0.9rem" }}
            >
              Apply
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TimePeriods;
